import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import PhotoLightbox from './PhotoLightbox';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

/* ─── Photo Carousel (used inside map popup) ─── */
export function PhotoCarousel({ photos, label, centroidLat, centroidLng }) {
  const [idx, setIdx] = useState(0);
  const count = photos.length;
  const p = photos[idx];
  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + count) % count); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % count); };
  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };
  const navBtn = (disabled) => ({
    background: disabled ? '#e5e7eb' : '#1a7431',
    color: disabled ? '#9ca3af' : '#fff',
    border: 'none', borderRadius: 6,
    padding: '6px 14px',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 16, fontWeight: 700, lineHeight: 1,
    minWidth: 40,
  });
  return (
    <div style={{ width: 280 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 8 }}>
        Centroid {centroidLat.toFixed(6)}, {centroidLng.toFixed(6)}
      </div>

      {/* Image */}
      <div style={{ borderRadius: 8, overflow: 'hidden', background: '#f3f4f6' }}>
        <img
          src={p.url}
          alt={p.type}
          style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
          onError={(ev) => { ev.target.style.display = 'none'; }}
        />
      </div>

      {/* Nav row: Prev | counter | Next */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 8, gap: 8,
      }}>
        <button type="button" onClick={prev} disabled={count <= 1} style={navBtn(count <= 1)} aria-label="Previous">
          &larr; Prev
        </button>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
          {idx + 1} / {count}
        </div>
        <button type="button" onClick={next} disabled={count <= 1} style={navBtn(count <= 1)} aria-label="Next">
          Next &rarr;
        </button>
      </div>

      {/* Details below */}
      <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.5, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
        <div style={{ textTransform: 'capitalize', fontWeight: 700, color: '#1a1a2e' }}>{p.type || 'Photo'}</div>
        {p.entryDate && (
          <div style={{ color: '#6b7280' }}>Captured: {formatDate(p.entryDate)}</div>
        )}
        <div style={{ color: '#374151', fontFamily: 'monospace', marginTop: 2 }}>
          {p.lat.toFixed(6)}, {p.lng.toFixed(6)}
        </div>
      </div>
    </div>
  );
}

/* ─── Monitoring Calendar ─── */
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function MonitoringCalendar({ entries }) {
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Build map of date → entries
  const dateMap = useMemo(() => {
    const map = {};
    (entries || []).forEach(e => {
      const d = e.monitoring_date;
      if (!d) return;
      const key = d.substring(0, 10); // YYYY-MM-DD
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [entries]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  // Build calendar grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = new Date().toISOString().substring(0, 10);

  // Selected date entries
  const selEntries = selectedDate ? (dateMap[selectedDate] || []) : [];

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>
        Monitoring History
      </div>

      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{
          background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 12px',
          cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151',
        }}>&larr;</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
          {MONTH_NAMES[month]} {year}
        </span>
        <button onClick={nextMonth} style={{
          background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 12px',
          cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151',
        }}>&rarr;</button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: '#9ca3af', padding: '4px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const hasEntries = !!dateMap[dateStr];
          const entryCount = hasEntries ? dateMap[dateStr].length : 0;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          // Color based on avg survival of entries on this day
          let dotColor = '#4caf50';
          if (hasEntries) {
            const rates = dateMap[dateStr].filter(e => e.survival_rate != null).map(e => e.survival_rate);
            const avg = rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : 100;
            if (avg < 70) dotColor = '#f44336';
            else if (avg < 85) dotColor = '#ff9800';
          }

          return (
            <div key={dateStr} onClick={() => hasEntries ? setSelectedDate(isSelected ? null : dateStr) : null}
              style={{
                textAlign: 'center', padding: '6px 2px', borderRadius: 8, cursor: hasEntries ? 'pointer' : 'default',
                background: isSelected ? '#e8f5e9' : isToday ? '#fff8e1' : '#fff',
                border: isSelected ? '2px solid #4caf50' : isToday ? '2px solid #ffc107' : '1px solid #f3f4f6',
                position: 'relative', transition: 'all 0.15s',
              }}>
              <div style={{ fontSize: 12, fontWeight: isToday || isSelected ? 700 : 400, color: hasEntries ? '#1a1a2e' : '#d1d5db' }}>
                {day}
              </div>
              {hasEntries && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 2 }}>
                  {entryCount <= 3 ? Array.from({ length: entryCount }).map((_, j) => (
                    <span key={j} style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
                  )) : (
                    <span style={{ fontSize: 8, fontWeight: 700, color: dotColor }}>{entryCount}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, color: '#6b7280', justifyContent: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4caf50', display: 'inline-block' }} /> Good (&ge;85%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff9800', display: 'inline-block' }} /> Attention (70-85%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f44336', display: 'inline-block' }} /> Critical (&lt;70%)
        </span>
      </div>

      {/* Selected date entries */}
      {selectedDate && selEntries.length > 0 && (
        <div style={{ marginTop: 12, background: '#f9fafb', borderRadius: 10, padding: 12, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>
            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>({selEntries.length} {selEntries.length === 1 ? 'entry' : 'entries'})</span>
          </div>
          {selEntries.map(e => {
            const survColor = (e.survival_rate || 0) >= 85 ? '#2e7d32' : (e.survival_rate || 0) >= 70 ? '#f57c00' : '#d32f2f';
            return (
              <div key={e.id} style={{
                background: '#fff', borderRadius: 8, padding: '10px 12px', marginBottom: 6,
                border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 8,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                    {e.zone_name || 'Unknown Zone'}
                    {e.agency_name && <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6, fontSize: 11 }}>by {e.agency_name}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {(e.total_trees_monitored || 0).toLocaleString()} trees |
                    Healthy: {e.trees_healthy || 0} |
                    Attention: {e.trees_needs_attention || 0} |
                    Critical: {e.trees_critical || 0} |
                    Dead: {e.trees_dead || 0}
                  </div>
                  {e.notes && <div style={{ fontSize: 11, color: '#374151', marginTop: 3, fontStyle: 'italic' }}>{e.notes}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: survColor }}>
                    {e.survival_rate != null ? `${e.survival_rate}%` : 'N/A'}
                  </div>
                  <div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'capitalize' }}>{e.overall_health || ''}</div>
                  <div style={{
                    display: 'inline-block', fontSize: 9, padding: '2px 6px', borderRadius: 4, marginTop: 2,
                    background: e.status === 'approved' ? '#e8f5e9' : e.status === 'rejected' ? '#fce4ec' : '#fff8e1',
                    color: e.status === 'approved' ? '#2e7d32' : e.status === 'rejected' ? '#c62828' : '#f57c00',
                    fontWeight: 600,
                  }}>{e.status}</div>
                </div>
              </div>
            );
          })}

          {/* Photo thumbnails for selected date */}
          {(() => {
            const datePhotos = selEntries.flatMap(e => (e.photos || []).map(p => ({ ...p, zone_name: e.zone_name })));
            if (datePhotos.length === 0) return null;
            return (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Photos ({datePhotos.length})</div>
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                  {datePhotos.map(p => (
                    <div key={p.id} style={{ flexShrink: 0, textAlign: 'center' }}>
                      <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                        style={{ width: 70, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                        onError={ev => { ev.target.style.display = 'none'; }} />
                      <div style={{ fontSize: 8, color: '#9ca3af', marginTop: 1, textTransform: 'capitalize' }}>{p.photo_type}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {selectedDate && selEntries.length === 0 && (
        <div style={{ marginTop: 12, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
          No monitoring entries on this date.
        </div>
      )}
    </div>
  );
}

/* Fly to selected zone bounds */
function ZoneFlyTo({ zones, entries, selectedZoneId }) {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedZoneId) return;
    const zone = (zones || []).find(z => z.id === selectedZoneId);
    if (!zone) return;
    const pts = [];
    if (zone.boundary_polygon) zone.boundary_polygon.forEach(p => pts.push([p.lat, p.lng]));
    if (zone.center_latitude && zone.center_longitude) pts.push([zone.center_latitude, zone.center_longitude]);
    (entries || []).filter(e => e.zone_id === selectedZoneId).forEach(e => {
      if (e.latitude && e.longitude) pts.push([e.latitude, e.longitude]);
      (e.photos || []).forEach(p => { if (p.latitude && p.longitude) pts.push([p.latitude, p.longitude]); });
    });
    if (pts.length > 0) {
      const b = L.latLngBounds(pts).pad(0.15);
      map.flyToBounds(b, { duration: 0.8, maxZoom: 18 });
    }
  }, [selectedZoneId, zones, entries, map]);
  return null;
}

const ZONE_COLORS = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];

/**
 * Rich Monitoring Map card — matches the Monitoring Progress page.
 * Props:
 *   fetchSummary  - function returning a promise of { data: { monitoring_zones, monitoring_entries, targets_info, ... } }
 *   title         - optional title override
 */
export default function MonitoringMapCard({ fetchSummary, title }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [healthFilter, setHealthFilter] = useState(null); // 'needs_attention' | 'critical' | 'dead' | null
  const [healthLightboxIdx, setHealthLightboxIdx] = useState(0);

  useEffect(() => {
    if (!fetchSummary) { setLoading(false); return; }
    fetchSummary()
      .then(res => setSummary(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchSummary]);

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px', textAlign: 'center', color: '#9ca3af' }}>
        Loading monitoring map...
      </div>
    );
  }

  if (!summary) return null;

  const zones = summary.monitoring_zones || [];
  const entries = summary.monitoring_entries || [];

  // Build zone color map
  const zoneColorMap = {};
  zones.forEach((z, i) => { zoneColorMap[z.id] = ZONE_COLORS[i % ZONE_COLORS.length]; });

  // Compute monitored count per zone from the LATEST APPROVED entry per zone
  const _mmcByZone = {};
  entries.forEach(e => {
    if (e.status !== 'approved' || !e.zone_id || !e.monitoring_date) return;
    const cur = _mmcByZone[e.zone_id];
    if (!cur || e.monitoring_date > cur.monitoring_date ||
        (e.monitoring_date === cur.monitoring_date && (e.id || 0) > (cur.id || 0))) {
      _mmcByZone[e.zone_id] = e;
    }
  });
  const zoneTreeCount = {};
  Object.values(_mmcByZone).forEach(e => {
    zoneTreeCount[e.zone_id] = e.total_trees_monitored || 0;
  });

  // Stats
  const totalTreesMonitored = summary.total_trees_monitored || 0;
  const totalPhotos = summary.total_photos || 0;
  const healthy = summary.trees_healthy || 0;
  const attention = summary.trees_needs_attention || 0;
  const critical = summary.trees_critical || 0;
  const dead = summary.trees_dead || 0;
  const healthPct = (v) => totalTreesMonitored > 0 ? ((v / totalTreesMonitored) * 100).toFixed(1) : '0.0';
  // Survival = (Healthy + Need Attention + Critical) / target planted total (sum of total_plants across projects)
  const targetsInfoArr = summary.targets_info || [];
  const targetTotal = targetsInfoArr.reduce((s, t) => s + (t.total_plants || 0), 0);
  const aliveTrees = healthy + attention + critical;
  const avgSurvival = targetTotal > 0 ? ((aliveTrees / targetTotal) * 100).toFixed(2) : 0;

  // Collect all map points for bounds
  const allPoints = [];
  zones.forEach(z => {
    if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
    if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
  });
  entries.forEach(e => {
    if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
    (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
  });

  const hasPoints = allPoints.length > 0;

  return (
    <div style={{
      background: '#fff', borderRadius: '14px', padding: '24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px',
    }}>
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>
        {title || 'Monitoring & GPS Mapping'}
      </div>
      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
        Real-time monitoring data from field visits
      </div>

      {/* Stats strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
        {[
          { label: 'Trees Monitored', value: totalTreesMonitored.toLocaleString(), color: '#1a7431', icon: '\uD83C\uDF33' },
          { label: 'Avg Survival', value: `${avgSurvival}%`, color: avgSurvival >= 80 ? '#2e7d32' : '#f57c00', icon: '\uD83D\uDCC8' },
          { label: 'Monitoring Zones', value: zones.length, color: '#1976d2', icon: '\uD83D\uDCCD' },
          { label: 'Geotagged Photos', value: totalPhotos, color: '#00897b', icon: '\uD83D\uDCF7' },
        ].map(s => (
          <div key={s.label} style={{
            background: `${s.color}08`, borderRadius: '10px', padding: '12px', textAlign: 'center',
            border: `1px solid ${s.color}20`,
          }}>
            <div style={{ fontSize: '18px', marginBottom: '2px' }}>{s.icon}</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tree Health Breakdown (Healthy / Attention / Critical / Dead) \u2014 click to see photos */}
      {(() => {
        // Collect sample photos per health category (from field-reporter's evidence photos)
        const photosByCategory = { needs_attention: [], critical: [], dead: [] };
        entries.forEach(e => {
          (e.photos || []).forEach(p => {
            if (p.photo_type !== 'sample') return;
            const desc = (p.description || '').toLowerCase();
            let cat = null;
            if (desc.includes('needs_attention') || desc.includes('attention')) cat = 'needs_attention';
            else if (desc.includes('critical')) cat = 'critical';
            else if (desc.includes('dead')) cat = 'dead';
            if (cat) {
              photosByCategory[cat].push({
                ...p,
                zone_name: e.zone_name,
                target_id: e.target_id,
                monitoring_date: e.monitoring_date,
                captured_at: p.captured_at || e.monitoring_date,
              });
            }
          });
        });
        const CATS = [
          { key: 'healthy', label: 'Healthy', value: healthy, color: '#2e7d32', icon: '\u2705', photos: [] },
          { key: 'needs_attention', label: 'Attention', value: attention, color: '#f57c00', icon: '\u26A0\uFE0F', photos: photosByCategory.needs_attention },
          { key: 'critical', label: 'Critical', value: critical, color: '#c62828', icon: '\u2757', photos: photosByCategory.critical },
          { key: 'dead', label: 'Dead', value: dead, color: '#616161', icon: '\u2620\uFE0F', photos: photosByCategory.dead },
        ];
        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {CATS.map(h => {
                const clickable = h.photos.length > 0;
                return (
                  <div key={h.label}
                    onClick={clickable ? () => { setHealthLightboxIdx(0); setHealthFilter(h.key); } : undefined}
                    title={clickable ? `View ${h.photos.length} evidence photo${h.photos.length > 1 ? 's' : ''}` : (h.key === 'healthy' ? 'Healthy trees do not require evidence photos' : 'No evidence photos yet')}
                    style={{
                      background: `${h.color}12`, borderRadius: '10px', padding: '10px 12px',
                      borderLeft: `4px solid ${h.color}`,
                      display: 'flex', alignItems: 'center', gap: '10px',
                      cursor: clickable ? 'pointer' : 'default',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={clickable ? (ev) => { ev.currentTarget.style.transform = 'translateY(-2px)'; ev.currentTarget.style.boxShadow = `0 4px 12px ${h.color}30`; } : undefined}
                    onMouseLeave={clickable ? (ev) => { ev.currentTarget.style.transform = 'translateY(0)'; ev.currentTarget.style.boxShadow = 'none'; } : undefined}
                  >
                    <span style={{ fontSize: '18px' }}>{h.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: h.color, lineHeight: 1.1 }}>
                        {h.value.toLocaleString()}
                        <span style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280', marginLeft: '6px' }}>
                          ({healthPct(h.value)}%)
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {h.label}
                        {clickable && (
                          <span style={{
                            fontSize: '9px', fontWeight: 700, color: '#fff', background: h.color,
                            padding: '1px 6px', borderRadius: '8px',
                          }}>
                            \uD83D\uDCF7 {h.photos.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Evidence lightbox */}
            {healthFilter && (() => {
              const cat = CATS.find(c => c.key === healthFilter);
              if (!cat || cat.photos.length === 0) return null;
              const catLabel = cat.label;
              return (
                <PhotoLightbox
                  photos={cat.photos}
                  initialIndex={healthLightboxIdx}
                  entryInfo={`${catLabel} trees \u2022 ${cat.photos.length} evidence photo${cat.photos.length > 1 ? 's' : ''}`}
                  onClose={() => setHealthFilter(null)}
                />
              );
            })()}
          </>
        );
      })()}

      {/* Map */}
      {hasPoints ? (() => {
        const bounds = L.latLngBounds(allPoints);
        return (
          <>
            <div style={{ height: '450px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <MapContainer
                bounds={bounds.pad(0.1)}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Esri"
                />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />

                {/* Zone polygons */}
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{
                      color: zoneColorMap[z.id],
                      weight: selectedZoneId === z.id ? 5 : 3,
                      fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2,
                    }}>
                    <Popup>
                      <strong>{z.name}</strong><br />
                      {(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees
                      {z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}
                    </Popup>
                  </Polygon>
                ))}

                {/* Photo markers — grouped by zone (single dot at centroid per zone) */}
                {(() => {
                  // Group geotagged photos by zone_id (fallback: entry id if no zone)
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });

                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });

                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="
                              background:${g.color};
                              color:#fff;
                              border-radius:50%;
                              width:${size}px;height:${size}px;
                              display:flex;align-items:center;justify-content:center;
                              border:3px solid #fff;
                              box-shadow:0 0 6px rgba(0,0,0,0.5);
                              font-size:${size >= 30 ? 13 : 11}px;
                              font-weight:700;
                              ${isSelected ? 'animation:pulse 1s infinite;' : ''}
                            ">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size / 2, size / 2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>

            {/* Clickable zone legend */}
            {zones.length > 0 && (
              <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
                <span onClick={() => setSelectedZoneId(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    padding: '4px 10px', borderRadius: 8,
                    background: !selectedZoneId ? '#e8f5e9' : '#fff',
                    border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0',
                    fontWeight: !selectedZoneId ? 700 : 400,
                  }}>
                  All Zones
                </span>
                {zones.map((z) => (
                  <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                      padding: '4px 10px', borderRadius: 8,
                      background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff',
                      border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0',
                      fontWeight: selectedZoneId === z.id ? 700 : 400,
                    }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                    {z.name} ({(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees)
                  </span>
                ))}
              </div>
            )}
          </>
        );
      })() : (
        <div style={{
          height: '360px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <MapContainer
            center={[22.5, 80]}
            zoom={5}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Esri"
            />
          </MapContainer>
        </div>
      )}

      {!hasPoints && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: '13px' }}>
          No GPS monitoring data available yet.
        </div>
      )}

      {/* Monitoring History Calendar */}
      {entries.length > 0 && <MonitoringCalendar entries={entries} />}
    </div>
  );
}
